import type {
  DefaultMeetingSession,
  AudioVideoEventAttributes,
  EventName,
  MeetingSessionStatus,
} from 'amazon-chime-sdk-js';
import { MeetingSessionStatusCode } from 'amazon-chime-sdk-js';

import {
  adaptHearing,
  determineCallDirection,
  getHearingPhoneNumber,
} from './VoiceMeetingServiceHelpers';

import type { Maybe } from 'common/types';
import type { VoiceSessionHearingParticipant } from 'features/voice-meeting/interfaces';
import { VoiceMeetingEventBus } from 'features/voice-meeting/services';
import type {
  Participant,
  SessionHearingParticipant,
  VoiceSessionResponse,
} from 'features/voice-session/interfaces';

/**
 * VoiceMeetingService manages voice meeting sessions and participants
 */
export class VoiceMeetingService {
  private static instance: VoiceMeetingService;
  public meetingSession: DefaultMeetingSession | null = null;
  public voiceSession: VoiceSessionResponse | null = null;

  private eventObserver: any = null;
  private audioVideoObserver: any = null;
  private attendeeIdPresenceHandler:
    | ((
        attendeeId: string,
        present: boolean,
        externalUserId?: string,
        dropped?: boolean
      ) => void)
    | null = null;
  private fatalErrorHandler: ((error: Error) => void) | null = null;

  private constructor() {}

  /**
   * Returns the singleton instance of the voice meeting service
   * @returns The VoiceMeetingService instance
   */
  public static getInstance(): VoiceMeetingService {
    if (!VoiceMeetingService.instance) {
      VoiceMeetingService.instance = new VoiceMeetingService();
    }

    return VoiceMeetingService.instance;
  }

  // ----------------------
  // Session Management
  // ----------------------

  /**
   * Sets the current meeting session
   * @param session - The meeting session to set
   */
  public setMeetingSession(session: DefaultMeetingSession | null): void {
    this.meetingSession = session;
  }

  /**
   * Sets the current voice session
   * @param sessionState - The voice session state to set
   */
  public setVoiceSession = (voiceSession: VoiceSessionResponse): void => {
    // Set the new voice session
    this.voiceSession = voiceSession;
  };

  /**
   * Returns the session ID from the current voice session
   * @returns The external meeting ID
   */
  public getSessionId = (): string =>
    this.voiceSession?.chimeMeeting.ExternalMeetingId;

  /**
   * Completes and cleans up the meeting session
   */
  public completeMeetingSession = (): void => {
    const meetingSession = this.meetingSession;
    const audioVideo = meetingSession?.audioVideo;

    if (!audioVideo || !meetingSession) {
      return;
    }

    this.tearDownListeners();

    // Destroy meeting session after audioVideo stopped. See: https://github.com/aws/amazon-chime-sdk-js/issues/2178
    const destroyObserver = {
      audioVideoDidStop() {
        audioVideo.removeObserver(destroyObserver);
        meetingSession.destroy();
      },
    };

    audioVideo.addObserver(destroyObserver);
    audioVideo.stop();
    this.meetingSession = null;
    this.voiceSession = null;
  };

  /**
   * Clears all participants from the voice session
   */
  public clearParticipants = (): void => {
    if (this.voiceSession) {
      this.voiceSession.participants = [];
    }
  };

  // ----------------------
  // Hearing Participant Methods
  // ----------------------

  /**
   * Gets a hearing participant by ID
   * @param id - The ID of the hearing participant to find
   * @returns The hearing participant or null if not found
   */
  public getHearingById = (
    id: string
  ): VoiceSessionHearingParticipant | null => {
    const hearing = this.voiceSession?.participants.find(
      (participant) => participant.type === 'hearing' && participant.id === id
    ) as Maybe<SessionHearingParticipant>;

    if (!hearing) {
      return null;
    }

    return adaptHearing(hearing);
  };

  /**
   * Gets a hearing participant by phone number
   * @param hearingPhoneNumber - The phone number to search for
   * @returns The hearing participant or null if not found
   */
  public getHearingByPhoneNumber = (
    hearingPhoneNumber: string
  ): VoiceSessionHearingParticipant | null => {
    const hearing = this.voiceSession?.participants.find(
      (participant) =>
        participant.type === 'hearing' &&
        getHearingPhoneNumber(participant) === hearingPhoneNumber
    ) as Maybe<SessionHearingParticipant>;

    if (!hearing) {
      return null;
    }

    return adaptHearing(hearing);
  };

  /**
   * Gets the first hearing participant that's not in disconnected state
   * @param participantId - Optional participant ID to filter by
   * @returns The first active hearing participant or null if none found
   */
  public getFirstNotDisconnectedHearing = (
    participantId?: string
  ): VoiceSessionHearingParticipant | null => {
    const participants: Participant[] = Object.assign(
      [],
      this.voiceSession?.participants
    ).reverse();

    const hearingParticipant = participants.find((participant) => {
      const baseSelector =
        participant.type === 'hearing' && participant.state !== 'disconnected';

      if (participantId) {
        return baseSelector && participant.id === participantId;
      }

      return baseSelector;
    }) as Maybe<SessionHearingParticipant>;

    return hearingParticipant ? adaptHearing(hearingParticipant) : null;
  };

  /**
   * Gets all hearing participants in the session
   * @returns Array of all hearing participants
   */
  public getHearings = (): VoiceSessionHearingParticipant[] => {
    const participants: Participant[] = this.voiceSession?.participants || [];

    const hearingParticipants = participants.filter((participant) => {
      return participant.type === 'hearing';
    });

    return hearingParticipants.map(adaptHearing);
  };

  /**
   * Gets all hearing participants that are not in disconnected state
   * @returns Array of active hearing participants
   */
  public getAllHearingsNotDisconnected =
    (): VoiceSessionHearingParticipant[] => {
      const participants: Participant[] = this.voiceSession?.participants || [];

      const hearingParticipants = participants.filter((participant) => {
        return (
          participant.type === 'hearing' && participant.state !== 'disconnected'
        );
      }) as SessionHearingParticipant[];

      return hearingParticipants.map(adaptHearing);
    };

  /**
   * Gets the first hearing participant in the session
   * @returns The first hearing participant or null if none found
   */
  public getFirstHearing = (): VoiceSessionHearingParticipant | null => {
    const hearing = this.voiceSession?.participants.find(
      (participant) => participant.type === 'hearing'
    ) as Maybe<SessionHearingParticipant>;

    if (!hearing) {
      return null;
    }

    return adaptHearing(hearing);
  };

  /**
   * Gets the direction of the current call
   * @param hearingId - Optional specific hearing ID to check
   * @returns The call direction or null if no session exists
   */
  public getCallDirection = (
    hearingId?: string
  ): 'inbound' | 'outbound' | null => {
    if (!this.voiceSession) {
      return null;
    }

    return determineCallDirection(this.voiceSession, hearingId);
  };

  // ----------------------
  // Event Handling
  // ----------------------

  /**
   * Sets up all event listeners for the meeting session
   * @throws Error if meeting session is not found
   */
  public setupListeners = (): void => {
    if (!this.meetingSession) {
      throw Error('DEBUG: Meeting session not found');
    }
    this.addEventObserver();
    this.addAudioVideoObserver();
    this.subscribeToAttendeeIdPresence();
    this.realtimeSubscribeToFatalError();
  };

  /**
   * Removes all event listeners from the meeting session
   */
  public tearDownListeners = (): void => {
    console.log('tearDownListeners');
    if (!this.meetingSession) {
      return;
    }

    // Remove event controller observer
    if (this.eventObserver) {
      this.meetingSession.eventController.removeObserver(this.eventObserver);
      this.eventObserver = null;
    }

    // Remove audio video observer
    if (this.audioVideoObserver) {
      this.meetingSession.audioVideo.removeObserver(this.audioVideoObserver);
      this.audioVideoObserver = null;
    }

    // Unsubscribe from attendee ID presence
    if (this.attendeeIdPresenceHandler) {
      this.meetingSession.audioVideo.realtimeUnsubscribeToAttendeeIdPresence(
        this.attendeeIdPresenceHandler
      );
      this.attendeeIdPresenceHandler = null;
    }

    // Unsubscribe from fatal errors
    if (this.fatalErrorHandler) {
      this.meetingSession.audioVideo.realtimeUnsubscribeToFatalError(
        this.fatalErrorHandler
      );
      this.fatalErrorHandler = null;
    }
  };

  /**
   * Registers a callback for when audio/video has started
   * @param callback - The function to call when audio/video starts
   */
  public onAudioVideoDidStart = (callback: () => void): void => {
    this.meetingSession?.audioVideo.addObserver({
      audioVideoDidStart: callback,
    });
  };

  /**
   * Sets up event observer for meeting events
   * @private
   */
  private readonly addEventObserver = (): void => {
    this.eventObserver = {
      eventDidReceive: (
        name: EventName,
        attributes: AudioVideoEventAttributes
      ) => {
        const meetingEventErrors = ['meetingStartFailed', 'meetingFailed'];
        if (meetingEventErrors.includes(name)) {
          VoiceMeetingEventBus.meetingError$.next({
            name,
            attributes,
          });
        }
      },
    };
    this.meetingSession!.eventController.addObserver(this.eventObserver);
  };

  /**
   * Sets up audio/video observer for session events
   * @private
   */
  private readonly addAudioVideoObserver = (): void => {
    this.audioVideoObserver = {
      audioVideoDidStop: (sessionStatus?: MeetingSessionStatus) => {
        if (
          sessionStatus &&
          VoiceMeetingService.isMeetingSessionFailed(sessionStatus)
        ) {
          VoiceMeetingEventBus.fatalError$.next(
            new Error('Meeting session failed')
          );
          return;
        }
        VoiceMeetingEventBus.finishVoiceSession$.next();
      },
    };
    this.meetingSession?.audioVideo.addObserver(this.audioVideoObserver);
  };

  /**
   * Determines if a meeting session has failed based on status code
   * @param sessionStatus - The meeting session status to check
   * @returns True if the session has failed
   * @private
   */
  private static isMeetingSessionFailed(
    sessionStatus: MeetingSessionStatus
  ): boolean {
    return (
      sessionStatus.statusCode() ===
        MeetingSessionStatusCode.AudioInternalServerError ||
      sessionStatus.statusCode() ===
        MeetingSessionStatusCode.AudioServiceUnavailable
    );
  }

  /**
   * Subscribes to attendee presence events
   * @private
   */
  private readonly subscribeToAttendeeIdPresence = (): void => {
    this.attendeeIdPresenceHandler = (
      attendeeId: string,
      present: boolean,
      externalUserId?: string,
      dropped?: boolean
    ) => {
      VoiceMeetingEventBus.attendeeIdPresence$.next({
        attendeeId,
        present,
        externalUserId,
        dropped,
      });
    };

    this.meetingSession?.audioVideo.realtimeSubscribeToAttendeeIdPresence(
      this.attendeeIdPresenceHandler
    );
  };

  /**
   * Subscribes to fatal error events
   * @private
   */
  private readonly realtimeSubscribeToFatalError = (): void => {
    this.fatalErrorHandler = (error: Error) => {
      VoiceMeetingEventBus.fatalError$.next(error);
    };

    this.meetingSession?.audioVideo.realtimeSubscribeToFatalError(
      this.fatalErrorHandler
    );
  };

  /**
   * Completely resets the service state to initial values
   * Call this at the end of a call to ensure clean state
   */
  public resetService = (): void => {
    if (this.meetingSession || this.voiceSession) {
      this.completeMeetingSession();
    } else {
      this.tearDownListeners();
    }

    this.meetingSession = null;
    this.voiceSession = null;
    this.eventObserver = null;
    this.audioVideoObserver = null;
    this.attendeeIdPresenceHandler = null;
    this.fatalErrorHandler = null;
  };
}
